const regexCurrency = /^\d+(?:\.\d{0,2})$/

export const leadingSlash = (str) => {
  return str.startsWith('/') ? str : '/' + str
}

export const trailingSlash = (str) => {
  return str.endsWith('/') ? str : str + '/'
}

export const wait = (timeout) => {
  return new Promise((resolve) => setTimeout(resolve, timeout))
}

export const normalizeTextSearch = (text) => {
  return String(text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

export const unmaskText = (text) => {
  if (!text) return

  let defaultDelimiters = /[-!$%^&*()_+|~=`{}[\]:";'<>?,./\\ ]/

  return text
    ? String(text).replace(new RegExp(defaultDelimiters, 'g'), '')
    : text
}

export const cleanObject = (object) => {
  Object.entries(object).forEach(([key, value]) => {
    if (value && typeof value === 'object') cleanObject(value)

    if ((value && typeof value === 'object' && !Object.keys(value).length) || value === null || value === undefined || value === '') {
      if (Array.isArray(object)) {
        object.splice(key, 1)
      } else {
        delete object[key]
      }
    }
  })

  return object
}

export const deepEqual = (x, y) => {
  const ok = Object.keys,
    tx = typeof x,
    ty = typeof y
  return x && y && tx === 'object' && tx === ty
    ? ok(x).length === ok(y).length &&
        ok(x).every((key) => deepEqual(x[key], y[key]))
    : x === y
}

export const errorMessages = (errors) => {
  return errors
    .map((error) => `<strong>${error.field}</strong>: ${error.message}<br />`)
    .join('')
}

// TODO: Find better way to check, convert and return BRL amounts
export const clearCurrency = (amount) => {
  if (!amount) return
  const stringAmount = String(amount)
  const currency = localStorage.getItem('user_currency')

  if (currency === 'BRL') return parseFloat(stringAmount.replace('R$','').replace('.','').replace(',','.')).toFixed(2)
  if (currency === 'AUD') return parseFloat(stringAmount.replace('A$','').replace(',','')).toFixed(2)
  if (currency === 'USD') return parseFloat(stringAmount.replace('$','').replace(',','')).toFixed(2)
  if (currency === 'EUR') return parseFloat(stringAmount.replace('€','').replace(',','')).toFixed(2)
}

export const checkCurrencyDecimals = (amount) => {
  if (!amount) return

  amount = amount.toString()

  if (regexCurrency.test(amount)) {
    const decimalDigits = amount.split('.')[1];
    if (decimalDigits && decimalDigits.length === 1) {
      return amount + '0';
    } else {
      return amount;
    }
  } else {
    return amount + '.00';
  }
}

// Valores já normalizados ('18.00', 18, undefined) viram número para comparação.
// O checkCurrencyDecimals devolve undefined quando o valor é 0, e o clearCurrency
// devolve undefined quando a moeda do usuário não está no localStorage: os dois
// casos viram NaN em parseFloat e precisam cair para 0.
export const toAmount = (amount) => {
  const parsed = parseFloat(amount)
  return Number.isFinite(parsed) ? parsed : 0
}

export const checkUserRole = (roles) => {
  const userRole = localStorage.getItem('access_roles')
  return roles.includes(userRole)
}

export const userHasSomeRole = (roles) => {
  const userRoles = localStorage.getItem('access_roles')?.split(',') ?? []
  return userRoles.some((userRole) => roles.includes(userRole.trim()))
}

export const generateExcelLink = (response, filename) => {
  let blob = new Blob([response.data], {type: 'application/vnd.ms-excel;charset=utf-8'})
  let url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', `${filename}.xlsx`)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const maskDate = (date, type) => {
  if (!date) return

  const parsedDate = unmaskText(date)

  return type === 'br'
    ? parsedDate.replace(/^(\d{4})(\d{2})(\d{2}).*/, '$3/$2/$1')
    : parsedDate.replace(/^(\d{2})(\d{2})(\d{4}).*/, '$3-$2-$1')
}

export const formatBirthDate = (date) => {
  if (!date) return
  return maskDate(date) + 'T12:00:00-03:00'
}

export const isObjectEmpty = (object) => {
  return Object.keys(object).length === 0 && object.constructor === Object;
}

/** O numero fica so com digitos: o backend agrupa cada pais do seu jeito -- `+55 (47) 99904-5517`,
 * `+61 455 896 669` -- e a mascara de exibicao e do componente, nao do valor. */
const onlyPhoneDigits = (value) => value.replace(/\D/g, '')

export const curatePhoneNumber = (phone) => {
  if (!phone) return ''

  const dddMatch = phone.match(/\+(\d{1,3})/)

  if (!dddMatch) {
    return {
      country_code: '+55',
      phone_number: onlyPhoneDigits(phone)
    }
  }

  return {
    country_code: dddMatch[0],
    phone_number: onlyPhoneDigits(phone.split(' ').slice(1).join(''))
  }
}

export const noSidebarInThisRoutes = [
  'login',
  'rootLogin',
  'recover-password',
  'reset-password',
  'onboarding',
  'onboardingSteps',
  'hostRating',
  'hostRatingDone',
  'quote',
  'confirmAppointment',
  'checkout',
  'view--error404',
]

export const noChatInThisRoutes = [
  'login',
  'rootLogin',
  'recover-password',
  'reset-password',
  'quote',
  'onboarding',
  'onboardingSteps',
  'hostRating',
  'hostRatingDone',
  'adminMap',
  'adminOperationMap',
  'adminGroupMap',
  'partnerMap',
  'hausefulUsers',
  'partnerAppointmentsList',
  'adminAppointmentsList',
  'adminAppointmentsDetails',
  'adminCustomerList',
  'adminInvoices',
  'adminDefaultServiceValues',
  'adminFinancial',
  'adminMissions',
  'adminMissionProgress',
  'adminMovements',
  'adminHosts',
  'adminHostOnboardingDetails',
  'adminCities',
  'adminHostAppointments',
  'adminCategories',
  'adminItems',
  'adminPriceList',
  'adminPaymentList',
  'adminVendors',
  'adminRecharge',
  'adminCharge',
  'adminSplitConfiguration',
  'adminRealEstateContractList',
  'adminTransfers',
  'adminContractDetails',
  'adminTransferDetails',
  'adminQuestionnaire',
  'adminQuestionnaireQuestions',
  'adminPartners',
  'adminAnnouncements',
  'adminHausefulInvoices',
  'adminHostsInvoices',
  'confirmAppointment',
  'checkout',
  'view--error404',
]

export const noNPSInThisRoutes = [
  'login',
  'rootLogin',
  'recover-password',
  'reset-password',
  'quote',
  'onboarding',
  'onboardingSteps',
  'hostRating',
  'hostRatingDone',
  'adminMap',
  'partnerMap',
  'hausefulUsers',
  'partnerAppointmentsList',
  'adminAppointmentsList',
  'adminAppointmentsDetails',
  'adminCustomerList',
  'adminInvoices',
  'adminDefaultServiceValues',
  'adminFinancial',
  'adminMissions',
  'adminMissionProgress',
  'adminHosts',
  'adminHostOnboardingDetails',
  'adminCities',
  'adminHostAppointments',
  'adminCategories',
  'adminItems',
  'adminMovements',
  'adminPriceList',
  'adminPaymentList',
  'adminRecharge',
  'adminCharge',
  'adminVendors',
  'adminSplitConfiguration',
  'adminRealEstateContractList',
  'adminTransfers',
  'adminContractDetails',
  'adminTransferDetails',
  'adminQuestionnaire',
  'adminQuestionnaireQuestions',
  'adminPartners',
  'adminAnnouncements',
  'adminHausefulInvoices',
  'adminHostsInvoices',
  'confirmAppointment',
  'checkout',
  'view--error404',
]
